import React, { useEffect, useState, useRef, useCallback, FC } from 'react';
import { isServerSide } from '~/src/helpers/ssr';
import * as ST from './styled';

interface ISlide {
    id: number,
    href: string,
    src: string,
    alt: string,
}

interface IOptions {
    slideWidth: number,
    interval: number,
    lentShift: number,
}

interface Props {
    slide: ISlide,
}

interface ICarouselTrackProps {
    readonly data: ISlide[],
    readonly options?: IOptions,
    readonly Content: FC<Props>,
}

const DefaultOptions = {
    SLIDE_WIDTH: 786,
    INTERVAL: 20,
    LENT_SHIFT: 1,
};

export const CarouselTrack = ({ data = [], options, Content }: ICarouselTrackProps) => {
    const [dataSlides, setDataSlides] = useState(data);
    const carouselRef = useRef<HTMLDivElement>(null);
    const timerId = useRef<number | undefined>(undefined);
    const observer = useRef<IntersectionObserver | null>(null);
    const slideRefs = useRef<HTMLDivElement[]>([]);

    const observeHandler = (entries: any[]) => {
        if (!entries[0].isIntersecting) {
            setDataSlides(dataSlides => {
                const newArray = dataSlides.slice(1);
                newArray.push(dataSlides[0]);
                return newArray;
            });
            const carousel = carouselRef?.current;
            if (carousel) {
                carousel.scrollLeft -= options?.slideWidth || DefaultOptions.SLIDE_WIDTH;
            }
        }
    };

    const initObserver = useCallback(() => {
        const carousel = carouselRef?.current;
        const config = {
            root: carousel,
            threshold: 0,
        };
        const obs = isServerSide() ? null : new IntersectionObserver(observeHandler, config);
        return obs;
    }, []);

    const setRef = (el: HTMLDivElement) => {
        slideRefs.current.push(el);
    };

    const shiftLent = () => {
        const carousel = carouselRef?.current;
        if (carousel) {
            carousel.scrollLeft += options?.lentShift || DefaultOptions.LENT_SHIFT;
        }
    };

    const startMove = () => {
        timerId.current = setInterval(shiftLent, options?.interval || DefaultOptions.INTERVAL);
    };

    const stopMove = () => {
        clearInterval(timerId.current);
    };

    useEffect(()=>{
        startMove();
        observer.current = initObserver();
        for (const el of slideRefs.current) {
            if (el) {
                observer.current?.observe(el);
            }
        }
        return () => {
            stopMove();
            observer.current?.disconnect();
        };
    }, []);

    return (
        <ST.CarouselTrack ref={carouselRef}>
            <ST.SlideLent>
                {dataSlides.map(slide => {
                    return (
                        <ST.SlideWrap
                            ref={setRef}
                            key={slide.id}
                            id={`${slide.id}`}
                            width={options?.slideWidth || DefaultOptions.SLIDE_WIDTH}
                            onMouseEnter={stopMove}
                            onMouseLeave={startMove}
                        >
                            <Content slide={slide} />
                        </ST.SlideWrap>
                    );
                })}
            </ST.SlideLent>
        </ST.CarouselTrack>
    );
};
